import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { savePendingVisits } from '../utils/pendingVisitSave';

const directBackend = process.env.NODE_ENV === 'development' ? String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') : '';
const API = directBackend ? `${directBackend}/api` : '/api';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000;
const IDLE_AUDIT_AFTER = 2 * 60 * 1000;
const WARNING_BEFORE_LOGOUT = 30 * 1000;
const ACTIVITY_HEARTBEAT_MS = 30 * 1000;
const UI_ACTION_DEDUPE_MS = 1200;
const ACTIVITY_KEY = 'jv_last_activity';
const CHECK_INTERVAL = 1000;
const INTERNAL_PATHS = ['/login', '/kiosk', '/launcher', '/staff', '/messages', '/analytics', '/bookings', '/settings', '/account-security', '/admin', '/activate-account', '/reset-password'];

const UI_VERB_ALIASES = {
  open: 'Open', view: 'View', save: 'Save', create: 'Create', new: 'Create', add: 'Add', edit: 'Edit',
  update: 'Update', delete: 'Delete', remove: 'Remove', send: 'Send', download: 'Download', upload: 'Upload',
  revoke: 'Revoke', restore: 'Restore', reset: 'Reset', enable: 'Enable', disable: 'Disable', unlock: 'Unlock',
  start: 'Start', complete: 'Complete', cancel: 'Cancel', confirm: 'Confirm', approve: 'Approve', reject: 'Reject',
  archive: 'Archive', mark: 'Mark', generate: 'Generate', copy: 'Copy',
};
const UI_NOUNS = new Set([
  'account', 'backup', 'booking', 'consent', 'course', 'department', 'device', 'document', 'form', 'intake',
  'link', 'media', 'message', 'page', 'passkey', 'patient', 'payment', 'post', 'profile', 'result', 'results',
  'service', 'session', 'treatment', 'user', 'visit',
]);

const isInternalRoute = () => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname || '/';
  return INTERNAL_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`));
};

const canonicalUiAction = value => {
  const words = String(value || '').toLowerCase().match(/[a-z0-9]+/g) || [];
  if (!words.length) return '';
  const verb = UI_VERB_ALIASES[words[0]];
  if (!verb) return '';
  const noun = words.slice(1).find(word => UI_NOUNS.has(word));
  return noun ? `${verb} ${noun.charAt(0).toUpperCase()}${noun.slice(1)}` : verb;
};

const uiActionFromClick = event => {
  if (typeof Element === 'undefined' || !(event?.target instanceof Element)) return '';
  const target = event.target.closest('button,[role="button"]');
  if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return '';
  const source = target.getAttribute('data-audit-action')
    || target.getAttribute('aria-label')
    || target.getAttribute('title')
    || target.textContent
    || '';
  return canonicalUiAction(source);
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const baseClient = () => axios.create({ baseURL: API, withCredentials: true });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managerPermissions, setManagerPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(30);

  const checkIntervalRef = useRef(null);
  const sessionRefreshVersion = useRef(0);
  const warningShownRef = useRef(false);
  const lastHeartbeatRef = useRef(0);
  const idleNotedRef = useRef(false);
  const lastAuditedActionRef = useRef({ key: '', at: 0 });

  const getLastActivity = () => {
    const stored = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
  };

  const updateLastActivity = (value = Date.now()) => localStorage.setItem(ACTIVITY_KEY, String(value));
  const getTimeRemaining = () => Math.max(0, INACTIVITY_TIMEOUT - (Date.now() - getLastActivity()));

  const clearLocalSession = useCallback(() => {
    sessionRefreshVersion.current += 1;
    sessionStorage.removeItem('jvc_recent_views_v1');
    localStorage.removeItem('jv_token');
    localStorage.removeItem(ACTIVITY_KEY);
    lastHeartbeatRef.current = 0;
    idleNotedRef.current = false;
    lastAuditedActionRef.current = { key: '', at: 0 };
    setUser(null);
    setManagerPermissions({});
    setPermissionsLoaded(false);
  }, []);

  const handleSessionRejected = useCallback(() => {
    clearLocalSession();
    if (isInternalRoute() && window.location.pathname !== '/login') window.location.replace('/login');
  }, [clearLocalSession]);

  const syncActivityTimestamp = useCallback(value => {
    const parsed = Date.parse(String(value || ''));
    if (Number.isFinite(parsed)) updateLastActivity(parsed);
  }, []);

  const sendServerActivity = useCallback(async (action = '', force = false, requestConfig = {}) => {
    if (!isInternalRoute() || typeof document === 'undefined') return null;
    if (document.visibilityState !== 'visible' || !document.hasFocus()) return null;

    const now = Date.now();
    const path = window.location.pathname || '/';
    const canonicalAction = canonicalUiAction(action);
    if (canonicalAction) {
      const key = `${canonicalAction}|${path}`;
      const previous = lastAuditedActionRef.current;
      if (previous.key === key && now - previous.at < UI_ACTION_DEDUPE_MS) return null;
      lastAuditedActionRef.current = { key, at: now };
    } else if (!force && now - lastHeartbeatRef.current < ACTIVITY_HEARTBEAT_MS) {
      return null;
    }

    lastHeartbeatRef.current = now;
    try {
      const response = await baseClient().post('/v2/auth/activity', {
        action: canonicalAction || undefined,
        path,
      }, requestConfig);
      if (requestConfig.signal?.aborted) return null;
      syncActivityTimestamp(response.data?.last_activity_at);
      if (response.data?.access_scope) setUser(current => {
        const scope = response.data.access_scope;
        if (!current || Object.keys(scope).every(key => JSON.stringify(current[key]) === JSON.stringify(scope[key]))) return current;
        return { ...current, ...scope };
      });
      idleNotedRef.current = false;
      return response.data;
    } catch (error) {
      if (!requestConfig.signal?.aborted && error.response?.status === 401) handleSessionRejected();
      return null;
    }
  }, [handleSessionRejected, syncActivityTimestamp]);

  const sendIdleNotice = useCallback(async () => {
    if (idleNotedRef.current || !isInternalRoute()) return;
    idleNotedRef.current = true;
    try {
      await baseClient().post('/v2/auth/idle');
    } catch (error) {
      if (error.response?.status === 401) {
        handleSessionRejected();
      } else {
        idleNotedRef.current = false;
      }
    }
  }, [handleSessionRejected]);

  const api = useCallback(() => {
    const instance = baseClient();
    instance.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) handleSessionRejected();
        return Promise.reject(error);
      }
    );
    return instance;
  }, [handleSessionRejected]);

  const refreshSession = useCallback(async (requestConfig = {}) => {
    const version = ++sessionRefreshVersion.current;
    const isCurrent = () => version === sessionRefreshVersion.current && !requestConfig.signal?.aborted;
    try {
      const response = await baseClient().get('/v2/auth/me', requestConfig);
      if (!isCurrent()) return null;
      let current = response.data;
      try {
        const profile = await baseClient().get('/v2/account-profile', requestConfig);
        current = { ...current, ...profile.data };
      } catch (profileError) {
        current = {
          ...current,
          login: current.login || current.username,
          user_name: current.user_name || current.username,
          mobile: current.mobile || '',
          email: current.email || '',
        };
      }
      if (!isCurrent()) return null;
      syncActivityTimestamp(current?.session_last_activity_at);
      setUser(current);
      return current;
    } catch (error) {
      if (!isCurrent()) return null;
      clearLocalSession();
      return null;
    }
  }, [clearLocalSession, syncActivityTimestamp]);

  const logout = useCallback(async (options = {}) => {
    if (options.force === true) {
      void savePendingVisits().catch(() => {});
    } else {
      try { await savePendingVisits(); } catch (_) { return false; }
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    warningShownRef.current = false;
    setShowInactivityWarning(false);
    try {
      await baseClient().post('/v2/auth/logout');
    } catch (error) {
      // Local UI logout still completes if the API is unavailable or the
      // server has already revoked the session for inactivity.
    }
    clearLocalSession();
    return true;
  }, [clearLocalSession]);

  const forceLogout = useCallback(() => {
    logout({ force: true }).finally(() => {
      if (isInternalRoute()) window.location.replace('/login');
    });
  }, [logout]);

  const login = async (username, password, onStage) => {
    try {
      onStage?.('checking_account');
      const response = await baseClient().post('/v2/auth/login/password', { username, password });
      const result = response.data;
      if (result.status !== 'AUTHENTICATED') return { success: false, error: 'Secure sign-in could not be completed.' };

      await onStage?.('opening_workspace', result);
      const current = await refreshSession();
      if (!current) return { success: false, error: 'Secure session could not be opened.' };
      updateLastActivity();
      await sendServerActivity('', true);
      setPermissionsLoaded(current.role === 'ADMIN');
      return { success: true, role: current.role, user: current };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Unable to sign in.',
      };
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword = newPassword) => {
    const response = await api().post('/v2/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  };

  const refreshManagerPermissions = useCallback(async () => {
    if (!user || user.role === 'KIOSK' || user.role === 'STAFF') {
      setManagerPermissions({});
      setPermissionsLoaded(true);
      return {};
    }

    setPermissionsLoading(true);
    try {
      const res = await api().get('/v2/manager-permissions');
      const permissions = res.data || {};
      setManagerPermissions(permissions);
      return permissions;
    } catch (error) {
      setManagerPermissions({});
      return {};
    } finally {
      setPermissionsLoading(false);
      setPermissionsLoaded(true);
    }
  }, [user, api]);

  const canAccess = useCallback(permission => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    if (user.role !== 'MANAGER') return false;
    if (!permission) return true;
    const blocked = new Set(user.restricted_access ? ['analytics', 'clinical_intakes', 'health_questions', 'users', 'login_audit', 'system_audit', 'gdpr'] : []);
    if (Array.isArray(permission)) return permission.some(key => !blocked.has(key) && managerPermissions?.[key] === true);
    if (blocked.has(permission)) return false;
    return managerPermissions?.[permission] === true;
  }, [user, managerPermissions]);

  useEffect(() => {
    if (user) {
      setPermissionsLoaded(user.role === 'ADMIN');
      refreshManagerPermissions();
    } else {
      setManagerPermissions({});
      setPermissionsLoaded(false);
    }
  }, [user?.username, user?.role, refreshManagerPermissions]);

  const stayLoggedIn = useCallback(() => {
    if (!isInternalRoute()) return;
    updateLastActivity();
    idleNotedRef.current = false;
    warningShownRef.current = false;
    setShowInactivityWarning(false);
    setWarningCountdown(30);
    sendServerActivity('', true);
  }, [sendServerActivity]);

  useEffect(() => {
    if (!user?.role) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      warningShownRef.current = false;
      idleNotedRef.current = false;
      setShowInactivityWarning(false);
      return;
    }

    if (user.role === 'KIOSK') {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      warningShownRef.current = false;
      idleNotedRef.current = false;
      setShowInactivityWarning(false);
      return;
    }

    const clearWarning = () => {
      warningShownRef.current = false;
      setShowInactivityWarning(false);
      setWarningCountdown(30);
    };

    const handleActivity = event => {
      if (!isInternalRoute()) {
        if (warningShownRef.current) clearWarning();
        return;
      }
      if (typeof document !== 'undefined' && (document.visibilityState !== 'visible' || !document.hasFocus())) return;
      if (getTimeRemaining() <= 0) {
        forceLogout();
        return;
      }
      updateLastActivity();
      idleNotedRef.current = false;
      clearWarning();
      const action = event?.type === 'click' ? uiActionFromClick(event) : '';
      sendServerActivity(action);
    };

    const checkInactivity = () => {
      if (!isInternalRoute()) {
        if (warningShownRef.current) clearWarning();
        return;
      }
      const elapsed = Date.now() - getLastActivity();
      const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
      if (elapsed >= IDLE_AUDIT_AFTER && !idleNotedRef.current) sendIdleNotice();
      if (remaining <= 0) {
        forceLogout();
        return;
      }
      if (remaining <= WARNING_BEFORE_LOGOUT) {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          setShowInactivityWarning(true);
          void savePendingVisits().catch(() => {});
        }
        setWarningCountdown(Math.ceil(remaining / 1000));
      } else if (warningShownRef.current) {
        clearWarning();
      }
    };

    window.addEventListener('click', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true, capture: true });
    window.addEventListener('focus', handleActivity);
    checkIntervalRef.current = setInterval(checkInactivity, CHECK_INTERVAL);
    checkInactivity();

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity, true);
      window.removeEventListener('focus', handleActivity);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [user?.role, forceLogout, sendIdleNotice, sendServerActivity]);

  useEffect(() => {
    const initialise = async () => {
      localStorage.removeItem('jv_token');
      await refreshSession();
      setLoading(false);
    };
    initialise();
  }, [refreshSession]);

  return (
    <AuthContext.Provider value={{
      user,
      token: user ? 'secure-session' : null,
      loading,
      login,
      logout,
      changePassword,
      refreshSession,
      api,
      isAdmin: user?.role === 'ADMIN',
      isManager: user?.role === 'MANAGER' || user?.role === 'ADMIN',
      isKiosk: user?.role === 'KIOSK',
      managerPermissions,
      permissionsLoading,
      permissionsLoaded,
      refreshManagerPermissions,
      canAccess,
      showInactivityWarning,
      warningCountdown,
      stayLoggedIn,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
